import { apiFetch } from '../../lib/apiFetch'
import { isDevMock } from '../../dev/isDevMock'
import {
  DEFAULT_ONBOARDING_CONFIG,
  loginChoiceSet,
  type ChatChoiceSet,
  type ChatFormRequest,
  type ChatStreamEvent,
  type GithubPanelFocus,
  type IssueTrackerProvider,
  type SummaryDestinationChannel,
  type OnboardingConfig,
  type ProjectChatRequestBody,
  type ProjectSettingsPatch,
} from '../../../netlify/functions/lib/project-chat-types'
import { normalizeGithubRepositoryUrl } from '../../lib/githubTesting'

// Streaming client for the `project-chat` Netlify function. The function streams newline-delimited
// JSON (one ChatStreamEvent per line); we read it incrementally and fan the events out to handlers.
// The NDJSON reader is adapted from replayio/nut.new's NutAPI.callNutAPI streaming path. In dev-mock
// mode (no backend) we synthesize a chunked stream so the streaming UI is still exercisable.

// Why a turn failed. Every one of these used to be indistinguishable from success or invisible:
//  - 'truncated' is the silent killer — the function is killed mid-turn (it has ~26s), the response body
//    just ends, and the old reader treated end-of-stream as a clean finish: spinner off, no reply, no
//    error, nothing persisted. It is the ONLY reason that reaches here with no `done` event.
//  - 'empty'     — the turn completed but produced no assistant text and opened no inline UI, so the
//                  user still got silence.
//  - 'stalled'   — reported as a warning while the turn is STILL RUNNING (it may yet succeed); the
//                  early-warning signal for turns creeping up on the execution limit.
export type ChatFailureReason = 'truncated' | 'empty' | 'server' | 'http' | 'parse' | 'network' | 'stalled'

/** What the client observed on the wire this turn — reported alongside every failure. */
export interface ChatTurnStats {
  /** Server correlation id from the `meta` event; null when the stream died before emitting it. */
  turnId: string | null
  elapsedMs: number
  bytesReceived: number
  eventsReceived: number
  /** Kind of the last event parsed, for pinpointing where a truncated stream stopped. */
  lastEventKind: string | null
  sawDone: boolean
  /** Any non-empty assistant text arrived. */
  sawText: boolean
  deltaChars: number
  /** An inline-UI/side-band event arrived (form, choices, GitHub panel, settings, …) — a turn that
   *  opens a panel is a real response even with no text, so it must not count as 'empty'. */
  sawUi: boolean
}

export class ChatStreamError extends Error {
  readonly reason: ChatFailureReason
  readonly stats: ChatTurnStats
  /** HTTP status when the request itself failed (reason 'http'). */
  readonly httpStatus?: number

  constructor(reason: ChatFailureReason, message: string, stats: ChatTurnStats, httpStatus?: number) {
    super(message)
    this.name = 'ChatStreamError'
    this.reason = reason
    this.stats = stats
    this.httpStatus = httpStatus
  }
}

// No bytes at all for this long, with the turn still open, raises the (non-fatal) stall warning. Well
// under the platform execution limit so it fires BEFORE a doomed turn is killed, not after.
const STALL_MS = 12_000

export interface ChatStreamHandlers {
  /** Incremental assistant text, accumulated client-side by message id. Streaming transport only. */
  onDelta: (id: string, text: string) => void
  /** The WHOLE assistant reply so far, replacing whatever is rendered. The polling transport
   *  (pollChat.ts) reports text this way — see replaceMessage in chatModel.ts for why. */
  onText?: (id: string, fullText: string) => void
  /** Nothing has arrived for STALL_MS and the turn is still open. Best-effort telemetry only — the
   *  turn is NOT aborted and may still complete normally. */
  onStall?: (stats: ChatTurnStats) => void
  onStatus?: (status: string) => void
  onConfig?: (config: OnboardingConfig) => void
  onReady?: () => void
  onForm?: (form: ChatFormRequest) => void
  onChoices?: (choices: ChatChoiceSet) => void
  /** Onboarding only: the user clearly asked to start QA — actually create/start the project now. */
  onStartQa?: () => void
  onSettings?: (patch: ProjectSettingsPatch, summary: string) => void
  /** The agent started real QA work (new exploration / journey re-run); re-fetch activity. */
  onRefresh?: () => void
  /** The agent asked to open the inline GitHub connect/disconnect panel (project chat only). `focus`
   *  targets the part of the flow the user asked about ('all' when unspecified). */
  onGithub?: (focus: GithubPanelFocus, repoUrl?: string, resumePrompt?: string) => void
  /** The agent asked to open the inline issue-tracker config panel (project chat only). `provider`
   *  preselects the tracker the user named, or undefined to let the panel default. */
  onIssueTracker?: (provider?: IssueTrackerProvider) => void
  /** The agent asked to open the inline daily-summary Share panel (project chat only). `channel`
   *  focuses the destination the user named, or undefined to let the panel default. */
  onSummaryDestination?: (channel?: SummaryDestinationChannel) => void
  /** The agent asked to pause or resume the project (project chat only). */
  onProjectStatus?: (status: 'active' | 'paused') => void
}

// Side-band events that are themselves a real response to the user (they reveal inline UI or apply a
// change), so a turn carrying one is not "empty" even with no assistant text. `config` is deliberately
// excluded: onboarding emits it on every turn, so counting it would mask genuinely empty replies.
const UI_EVENT_KINDS = new Set(['form', 'choices', 'settings', 'github', 'issue_tracker', 'summary_destination', 'project_status', 'ready', 'start_qa', 'refresh'])

function dispatch(event: ChatStreamEvent, handlers: ChatStreamHandlers, stats: ChatTurnStats) {
  stats.eventsReceived += 1
  stats.lastEventKind = event.kind
  if (UI_EVENT_KINDS.has(event.kind)) stats.sawUi = true
  switch (event.kind) {
    // Recorded on `stats` only; callers read it back from the resolved stats or ChatStreamError.stats.
    case 'meta': stats.turnId = event.turnId; break
    case 'delta':
      if (event.text) { stats.sawText = true; stats.deltaChars += event.text.length }
      handlers.onDelta(event.id, event.text)
      break
    case 'status': handlers.onStatus?.(event.status); break
    case 'config': handlers.onConfig?.(event.config); break
    case 'ready': handlers.onReady?.(); break
    case 'start_qa': handlers.onStartQa?.(); break
    case 'form': handlers.onForm?.(event.form); break
    case 'choices': handlers.onChoices?.(event.choices); break
    case 'settings': handlers.onSettings?.(event.patch, event.summary); break
    case 'refresh': handlers.onRefresh?.(); break
    case 'github': handlers.onGithub?.(event.focus ?? 'all', event.repoUrl, event.resumePrompt); break
    case 'issue_tracker': handlers.onIssueTracker?.(event.provider); break
    case 'summary_destination': handlers.onSummaryDestination?.(event.channel); break
    case 'project_status': handlers.onProjectStatus?.(event.status); break
    case 'error': throw new ChatStreamError('server', event.error, stats)
    case 'done': stats.sawDone = true; break
  }
}

// Read the NDJSON turn to completion, or throw a ChatStreamError describing exactly how it failed.
// Resolving means the user actually got a response: `done` arrived AND there was either assistant text
// or an inline-UI event. Every other outcome throws, so no failure can reach the user as silence.
export async function streamChat(body: ProjectChatRequestBody, handlers: ChatStreamHandlers): Promise<ChatTurnStats> {
  if (isDevMock()) {
    await mockStream(body, handlers)
    return { turnId: 'dev-mock', elapsedMs: 0, bytesReceived: 0, eventsReceived: 0, lastEventKind: 'done', sawDone: true, sawText: true, deltaChars: 0, sawUi: false }
  }

  const startedAt = Date.now()
  const stats: ChatTurnStats = {
    turnId: null, elapsedMs: 0, bytesReceived: 0, eventsReceived: 0, lastEventKind: null,
    sawDone: false, sawText: false, deltaChars: 0, sawUi: false,
  }
  const finish = (): ChatTurnStats => { stats.elapsedMs = Date.now() - startedAt; return stats }

  let res: Response
  try {
    res = await apiFetch('/api/project-chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch (e) {
    throw new ChatStreamError('network', e instanceof Error ? e.message : 'Network request failed', finish())
  }
  if (!res.ok || !res.body) {
    let detail = ''
    try { detail = ((await res.json()) as { error?: string }).error ?? '' } catch { /* non-JSON */ }
    throw new ChatStreamError('http', detail || `Chat request failed (${res.status})`, finish(), res.status)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  // Stall watchdog: a warning fired once when nothing has arrived for STALL_MS. Deliberately does NOT
  // abort — the turn may still land — it just makes near-limit turns visible before they get killed.
  // Created after the reader so there is no path from here to the finally that could leak the interval.
  let lastByteAt = Date.now()
  let stallReported = false
  const stallTimer = setInterval(() => {
    if (stallReported || Date.now() - lastByteAt < STALL_MS) return
    stallReported = true
    try { handlers.onStall?.({ ...stats, elapsedMs: Date.now() - startedAt }) } catch { /* telemetry must never break the turn */ }
  }, 2000)

  try {
    for (;;) {
      let chunk: ReadableStreamReadResult<Uint8Array>
      try {
        chunk = await reader.read()
      } catch (e) {
        throw new ChatStreamError('network', e instanceof Error ? e.message : 'Connection dropped', finish())
      }
      if (chunk.done) break
      lastByteAt = Date.now()
      stats.bytesReceived += chunk.value?.byteLength ?? 0
      buffer += decoder.decode(chunk.value, { stream: true })
      let nl: number
      while ((nl = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, nl).trim()
        buffer = buffer.slice(nl + 1)
        if (line) dispatch(parseEvent(line), handlers, stats)
      }
    }
    const tail = buffer.trim()
    if (tail) dispatch(parseEvent(tail), handlers, stats)
  } catch (e) {
    if (e instanceof ParseFailure) throw new ChatStreamError('parse', 'The reply stream was malformed.', finish())
    throw e
  } finally {
    clearInterval(stallTimer)
    // Stamp the duration here so EVERY exit path — including a ChatStreamError thrown out of dispatch —
    // carries it, rather than reporting elapsedMs 0.
    stats.elapsedMs = Date.now() - startedAt
  }

  // No `done` means the server never got to finish: the function was killed mid-turn (execution limit)
  // or the connection was cut. This is the case that used to look exactly like success.
  if (!stats.sawDone) {
    throw new ChatStreamError('truncated', 'The reply was cut off before it finished.', finish())
  }
  // Finished cleanly but said nothing and opened nothing — still silence from the user's point of view.
  if (!stats.sawText && !stats.sawUi) {
    throw new ChatStreamError('empty', 'The assistant finished without saying anything.', finish())
  }
  return finish()
}

function parseEvent(line: string): ChatStreamEvent {
  try {
    return JSON.parse(line) as ChatStreamEvent
  } catch {
    throw new ParseFailure()
  }
}

// Thrown by parseEvent, converted to a ChatStreamError (with the turn's stats attached) by the caller.
class ParseFailure extends Error {}

// ---- Dev-mock scripted, chunked stream (no LLM / backend) ----------------------------------------

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

async function streamText(id: string, text: string, onDelta: (id: string, t: string) => void) {
  // Emit a few words at a time so the streaming path (merge-by-id) is exercised.
  const words = text.split(' ')
  for (let i = 0; i < words.length; i += 3) {
    onDelta(id, (i === 0 ? '' : ' ') + words.slice(i, i + 3).join(' '))
    await delay(30)
  }
}

// Dev-mock entry point for the POLLING transport (pollChat.ts). Same scripted stream, wrapped in the
// stats shape runChatTurn resolves with, so dev-mock exercises the same call site as production.
export async function mockChatTurn(body: ProjectChatRequestBody, handlers: ChatStreamHandlers): Promise<ChatTurnStats> {
  await mockStream(body, handlers)
  return {
    turnId: 'dev-mock', elapsedMs: 0, bytesReceived: 0, eventsReceived: 0, lastEventKind: 'done',
    sawDone: true, sawText: true, deltaChars: 0, sawUi: false,
  }
}

async function mockStream(body: ProjectChatRequestBody, handlers: ChatStreamHandlers) {
  const id = `mock-${Date.now()}`
  const last = body.messages[body.messages.length - 1]?.content.toLowerCase() ?? ''

  if (body.mode === 'onboarding') {
    const config = { ...DEFAULT_ONBOARDING_CONFIG, ...body.config }
    if (/\b(login|credential|sign ?in|password)\b/.test(last)) {
      handlers.onChoices?.(loginChoiceSet('How would you like to handle logins?'))
      await streamText(id, '(dev-mock) This app has signed-in areas — how would you like to handle logins?', handlers.onDelta)
      return
    }
    if (/\b(start|go|create it|looks good|confirm|ready)\b/.test(last)) {
      handlers.onReady?.()
      handlers.onConfig?.(config)
      handlers.onStartQa?.()
      await streamText(id, '(dev-mock) Great — starting your QA project now!', handlers.onDelta)
      return
    }
    if (/\bfocus\b/.test(last)) {
      const next = { ...config, scopeMode: 'focus' as const, instructions: last }
      handlers.onConfig?.(next)
      await streamText(id, "(dev-mock) Got it — I'll focus the exploration there. Anything else, or shall I start?", handlers.onDelta)
      return
    }
    if (/\bbudget\b/.test(last)) {
      await streamText(id, '(dev-mock) About 10 credits covers a smoke test; 20–50 is the sweet spot for thorough testing; 50+ suits large apps. It caps spend and can change anytime.', handlers.onDelta)
      return
    }
    handlers.onConfig?.(config)
    await streamText(id, '(dev-mock) You can set the test scope, add logins, a design doc, or a budget — or just say "start" and I\'ll begin.', handlers.onDelta)
    return
  }

  // Project mode
  if (/\b(login|credential|password)\b/.test(last)) {
    handlers.onChoices?.(loginChoiceSet('How would you like to handle logins?'))
    await streamText(id, '(dev-mock) I can help with logins — how would you like to handle them?', handlers.onDelta)
    return
  }
  if (/\b(issue tracker|issue-tracker|linear|jira|gitlab|file (a )?bug|file bugs|file issues?|report bugs?|send bugs?)\b/.test(last)) {
    const provider: IssueTrackerProvider | undefined = /\blinear\b/.test(last) ? 'linear' : /\bjira\b/.test(last) ? 'jira' : /\bgitlab\b/.test(last) ? 'gitlab' : /\bgithub\b/.test(last) ? 'github' : undefined
    handlers.onIssueTracker?.(provider)
    await streamText(id, `(dev-mock) Sure — use the issue-tracker panel below to connect ${provider ?? 'GitHub, GitLab, Linear, or Jira'} and file bugs there.`, handlers.onDelta)
    return
  }
  if (/\b(github|repo|repository|disconnect|connect|pull request|pull-request|\bpr\b|\bprs\b|main branch|main-branch|deployment)\b/.test(last)) {
    // Mirror the agent's `focus` targeting: only the part the user named opens straight to that
    // question; a general GitHub ask (or connect/disconnect) opens the whole panel.
    const mentionsPr = /\b(pull request|pull-request|pr|prs)\b/.test(last)
    const mentionsMain = /\b(main branch|main-branch|deployment)\b/.test(last)
    const focus: GithubPanelFocus = mentionsPr && !mentionsMain ? 'pull-requests' : mentionsMain && !mentionsPr ? 'main-branch' : 'all'
    const repositoryText = last.match(/(?:https?:\/\/)?(?:www\.)?github\.com\/[a-z0-9][a-z0-9-]*\/[a-z0-9._-]+|\b[a-z0-9][a-z0-9-]*\/[a-z0-9._-]+\b/i)?.[0]
    handlers.onGithub?.(focus, repositoryText ? normalizeGithubRepositoryUrl(repositoryText) ?? undefined : undefined)
    const what = focus === 'pull-requests' ? 'change pull-request testing' : focus === 'main-branch' ? 'change main-branch testing' : 'connect or disconnect a repository'
    await streamText(id, `(dev-mock) Sure — use the GitHub panel below to ${what}.`, handlers.onDelta)
    return
  }
  if (/\b(pause|stop|hold)\b/.test(last)) {
    handlers.onProjectStatus?.('paused')
    await streamText(id, '(dev-mock) Pausing the project now — QA won’t start new work until you resume it.', handlers.onDelta)
    return
  }
  if (/\b(resume|unpause|restart|continue)\b/.test(last)) {
    handlers.onProjectStatus?.('active')
    await streamText(id, '(dev-mock) Resuming the project now — QA is picking work back up.', handlers.onDelta)
    return
  }
  if (/\b(qa pass|explore|exploration|run qa|another pass|scan)\b/.test(last)) {
    handlers.onRefresh?.()
    await streamText(id, "(dev-mock) Started a new exploration — a QA agent is now browsing the app. It'll show up in the activity timeline.", handlers.onDelta)
    return
  }
  if (/\b(re-?run|retrigger|run again)\b/.test(last)) {
    handlers.onRefresh?.()
    await streamText(id, '(dev-mock) Queued a fresh run of that journey — watch the activity timeline.', handlers.onDelta)
    return
  }
  if (/\b(transcript|thoughts|what did .*(do|find)|reasoning)\b/.test(last)) {
    await streamText(id, "(dev-mock) Here's what that exploration did: it opened the app, signed in, browsed the dashboard, and filed one layout bug.", handlers.onDelta)
    return
  }
  await streamText(id, `(dev-mock) You said: "${body.messages[body.messages.length - 1]?.content ?? ''}". I can run a new QA pass, re-run a journey, read an exploration's transcript, answer questions about journeys/bugs/activity, or change settings.`, handlers.onDelta)
}
